import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Badge } from '../../components/ui/badge.js';
import FeatureImage from './FeatureImage.js';

interface TelecomProvider {
  name: string;
  logo: string;
  features: string[];
  price: string;
  buttonText: string;
  plans: TelecomPlan[];
  disabled?: boolean;
}

interface TelecomPlan {
  name: string;
  price: string;
  data: string;
  talk: string;
  features: string[];
  benefits: string[];
  restrictions: string[];
}

const telecomProviders: TelecomProvider[] = [
  {
    name: "Rogers",
    logo: "/images/Rogers-Logo.png",
    features: [
      "5G Network Coverage",
      "Unlimited Data Plans",
      "Family Sharing Options",
      "Device Financing"
    ],
    price: "Starting at $55/mo",
    buttonText: "View Plans",
    disabled: true,
    plans: [
      {
        name: "Unlimited Plus",
        price: "$55/mo",
        data: "Unlimited",
        talk: "Unlimited",
        features: [
          "5G Network",
          "Unlimited Canada-wide calling",
          "Unlimited texts",
          "Unlimited data",
          "50GB international data"
        ],
        benefits: [
          "Free Netflix Basic",
          "25% off device purchases",
          "10GB international data"
        ],
        restrictions: [
          "Speed managed after 50GB",
          "Data usage capped at 1TB",
          "International data not available in all countries"
        ]
      },
      {
        name: "Premier",
        price: "$75/mo",
        data: "Unlimited",
        talk: "Unlimited",
        features: [
          "5G Network",
          "Unlimited Canada-wide calling",
          "Unlimited texts",
          "Unlimited data",
          "100GB international data"
        ],
        benefits: [
          "Free Netflix Premium",
          "40% off device purchases",
          "20GB international data"
        ],
        restrictions: [
          "Speed managed after 100GB",
          "Data usage capped at 2TB",
          "International data not available in all countries"
        ]
      }
    ]
  },
  {
    name: "Chatr",
    logo: "/images/chatr-logo.png",
    features: [
      "Nationwide Coverage",
      "No Credit Check",
      "Talk & Text Plans",
      "Data Plans"
    ],
    price: "Starting at $25/mo",
    buttonText: "View Plans",
    disabled: true,
    plans: [
      {
        name: "Chatr 5GB",
        price: "$25/mo",
        data: "5GB",
        talk: "Unlimited",
        features: [
          "Nationwide coverage",
          "Unlimited Canada-wide calling",
          "Unlimited texts",
          "5GB data"
        ],
        benefits: [
          "No credit check required",
          "No contract",
          "No setup fee"
        ],
        restrictions: [
          "Data usage capped at 5GB",
          "Speed managed after 5GB",
          "No international data"
        ]
      },
      {
        name: "Chatr 10GB",
        price: "$40/mo",
        data: "10GB",
        talk: "Unlimited",
        features: [
          "Nationwide coverage",
          "Unlimited Canada-wide calling",
          "Unlimited texts",
          "10GB data"
        ],
        benefits: [
          "No credit check required",
          "No contract",
          "No setup fee"
        ],
        restrictions: [
          "Data usage capped at 10GB",
          "Speed managed after 10GB",
          "No international data"
        ]
      }
    ]
  },
  {
    name: "Fido",
    logo: "/images/fido-logo.png",
    features: [
      "4G LTE Network",
      "Data Plans",
      "Device Financing",
      "International Options"
    ],
    price: "Starting at $45/mo",
    buttonText: "View Plans",
    disabled: true,
    plans: [
      {
        name: "Fido Starter",
        price: "$45/mo",
        data: "10GB",
        talk: "Unlimited",
        features: [
          "4G LTE Network",
          "Unlimited Canada-wide calling",
          "Unlimited texts",
          "10GB data"
        ],
        benefits: [
          "No contract",
          "No setup fee",
          "10% off device purchases"
        ],
        restrictions: [
          "Data usage capped at 10GB",
          "Speed managed after 10GB",
          "No international data"
        ]
      },
      {
        name: "Fido Plus",
        price: "$65/mo",
        data: "Unlimited",
        talk: "Unlimited",
        features: [
          "4G LTE Network",
          "Unlimited Canada-wide calling",
          "Unlimited texts",
          "Unlimited data",
          "25GB international data"
        ],
        benefits: [
          "No contract",
          "No setup fee",
          "10% off device purchases"
        ],
        restrictions: [
          "Data usage capped at 10GB",
          "Speed managed after 10GB",
          "No international data"
        ]
      },
      {
        name: "Fido Plus",
        price: "$65/mo",
        data: "Unlimited",
        talk: "Unlimited",
        features: [
          "4G LTE Network",
          "Unlimited Canada-wide calling",
          "Unlimited texts",
          "Unlimited data",
          "25GB international data"
        ],
        benefits: [
          "No contract",
          "No setup fee",
          "20% off device purchases"
        ],
        restrictions: [
          "Speed managed after 25GB",
          "Data usage capped at 1TB",
          "International data not available in all countries"
        ]
      }
    ]
  },
  {
    name: "Koodo Prepaid",
    logo: "/images/koodo-logo.png", // Add this image to your public/images folder if not present
    features: [
      "No credit check",
      "Keep your number",
      "No contract",
      "Nationwide coverage"
    ],
    price: "Starting at $15/mo",
    buttonText: "View Plans",
    disabled: true,

    plans: [
      {
        name: "$15 Prepaid Base Plan",
        price: "$15/mo",
        data: "250 MB (with auto top-up)",
        talk: "100 minutes",
        features: [
          "Unlimited international text",
          "100 Canada-wide minutes",
          "250 MB data with auto top-up"
        ],
        benefits: [
          "No contract",
          "No credit check",
          "Earn boosters for more data/talk"
        ],
        restrictions: [
          "Data only with auto top-up",
          "Additional minutes/data require boosters"
        ]
      },
      {
        name: "$25 Prepaid Base Plan",
        price: "$25/mo",
        data: "1 GB (with auto top-up)",
        talk: "Unlimited",
        features: [
          "Unlimited international text",
          "Unlimited Canada-wide minutes",
          "1 GB data with auto top-up"
        ],
        benefits: [
          "No contract",
          "No credit check",
          "Earn boosters for more data/talk"
        ],
        restrictions: [
          "Data only with auto top-up",
          "Additional data require boosters"
        ]
      },
      {
        name: "$35 Prepaid Base Plan",
        price: "$35/mo",
        data: "3 GB (with auto top-up)",
        talk: "Unlimited",
        features: [
          "Unlimited international text",
          "Unlimited Canada-wide minutes",
          "3 GB data with auto top-up"
        ],
        benefits: [
          "No contract",
          "No credit check",
          "Earn boosters for more data/talk"
        ],
        restrictions: [
          "Data only with auto top-up",
          "Additional data require boosters"
        ]
      },
      {
        name: "$50 Prepaid Base Plan",
        price: "$50/mo",
        data: "10 GB (with auto top-up)",
        talk: "Unlimited",
        features: [
          "Unlimited international text",
          "Unlimited Canada-wide minutes",
          "10 GB data with auto top-up"
        ],
        benefits: [
          "No contract",
          "No credit check",
          "Earn boosters for more data/talk"
        ],
        restrictions: [
          "Data only with auto top-up",
          "Additional data require boosters"
        ]
      }
    ]
  },
  // NOTE: Update the Koodo plans above with the latest details from https://www.koodomobile.com/en/prepaid-plans as needed.
];

export default function TelecomServices() {
  const [selectedPlan, setSelectedPlan] = useState<TelecomPlan | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleSelectPlan = (plan: TelecomPlan) => {
    setSelectedPlan(plan);
  };

  const handleCloseModal = () => {
    setSelectedPlan(null);
  };

  // ... (keep your existing scroll handlers and useEffect)

  return (
    <section id="telecom" className="py-12">
      <div className="container">
        <FeatureImage />
        <div className="text-center mb-8">
          <h2 className="section-title flex items-center gap-2 justify-center text-2xl font-semibold">
            <i className="fa-solid fa-tower-cell text-primary"></i>
            Telecom Services
          </h2>
          <p className="text-gray-600 mt-2">
            Connect with the best telecom providers
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {telecomProviders.filter(provider => !provider.disabled).map((provider, idx) => (
            <div
              key={provider.name + idx}
              className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col h-full"
            >
              <div className="telecom-logo mb-4">
                <img
                  src={provider.logo}
                  alt={provider.name}
                  className="w-32 h-16 object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold mb-2">{provider.name}</h3>
              <p className="text-primary mb-4">{provider.price}</p>
              <ul className="space-y-2 mb-4">
                {provider.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-primary"></i>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 transition-colors">
                    {provider.buttonText}
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-4xl md:max-w-5xl lg:max-w-6xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl sm:text-2xl md:text-3xl">
                      {provider.name} Plans
                    </DialogTitle>
                    <DialogDescription className="text-sm sm:text-base md:text-lg">
                      Choose the perfect plan for your needs
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="py-4">
                    <div
                      ref={modalRef}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                      {provider.plans.map((plan) => (
                        <div
                          key={plan.name}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">{plan.name}</h3>
                            <span className="text-lg font-bold text-primary">{plan.price}</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <div>
                              <h4 className="font-medium text-sm">Data</h4>
                              <p className="text-primary">{plan.data}</p>
                            </div>
                            <div>
                              <h4 className="font-medium text-sm">Talk</h4>
                              <p className="text-primary">{plan.talk}</p>
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <h4 className="font-medium text-sm mb-1">Features</h4>
                            <ul className="space-y-1">
                              {plan.features.map((feature, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-primary mr-1">•</span>
                                  <span className="text-sm">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="mb-3">
                            <h4 className="font-medium text-sm mb-1">Benefits</h4>
                            <ul className="space-y-1">
                              {plan.benefits.map((benefit, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-primary mr-1">•</span>
                                  <span className="text-sm">{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="mb-3">
                            <h4 className="font-medium text-sm mb-1">Restrictions</h4>
                            <ul className="space-y-1">
                              {plan.restrictions.map((restriction, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-primary mr-1">•</span>
                                  <span className="text-sm">{restriction}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <Button
                            className="w-full mt-2"
                            onClick={() => handleSelectPlan(plan)}
                          >
                            Select Plan
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          ))}
        </div>
      </div>
    
    {/* Plan Details Modal */}
    {selectedPlan && (
      <Dialog open={!!selectedPlan} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPlan?.name} Plan Details</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Plan Overview</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">Monthly Price</h4>
                  <p className="text-xl font-bold text-primary">{selectedPlan?.price ?? "N/A"}</p>
                </div>
                <div>
                  <h4 className="font-medium">Data Allowance</h4>
                  <p className="text-primary">{selectedPlan?.data ?? "N/A"}</p>
                </div>
                <div>
                  <h4 className="font-medium">Talk & Text</h4>
                  <p className="text-primary">{selectedPlan?.talk ?? "N/A"}</p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-3">Plan Features</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">Key Features</h4>
                  <ul className="list-disc list-inside pl-5 space-y-1">
                    {selectedPlan?.features?.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium">Exclusive Benefits</h4>
                  <ul className="list-disc list-inside pl-5 space-y-1">
                    {selectedPlan?.benefits?.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium">Usage Restrictions</h4>
                  <ul className="list-disc list-inside pl-5 space-y-1">
                    {selectedPlan?.restrictions?.map((restriction, index) => (
                      <li key={index}>{restriction}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={handleCloseModal}>
              Back to Plans
            </Button>
            <Button className="bg-primary hover:bg-primary/90">
              Book This Plan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </section>
  );
}